import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Plus, Search, Eye, CheckCircle, XCircle, Ban,
    RotateCcw, ChevronLeft, ChevronRight, X, Wallet,
    ArrowLeft, Building2, Mail, Hash, Clock,
} from 'lucide-react';

function PartnerManagement() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [partners, setPartners] = React.useState<any[]>([]);
    const [summary, setSummary] = React.useState<any>(null);
    const [meta, setMeta] = React.useState<any>(null);
    const [page, setPage] = React.useState<number>(1);
    const [search, setSearch] = React.useState<string>('');
    const [statusFilter, setStatusFilter] = React.useState<string>('');

    // Modals
    const [showCreateModal, setShowCreateModal] = React.useState<boolean>(false);
    const [showWalletModal, setShowWalletModal] = React.useState<any>(null); // { partnerId, type: 'credit'|'debit' }
    const [showActionModal, setShowActionModal] = React.useState<any>(null); // { partnerId, action, partnerName }
    const [actionLoader, setActionLoader] = React.useState<boolean>(false);

    // Create form
    const [createForm, setCreateForm] = React.useState({ company_name: '', contact_email: '', rate_limit_per_minute: 120 });
    // Wallet form
    const [walletForm, setWalletForm] = React.useState({ amount: '', description: '', reference: '' });
    // Reject/Suspend reason
    const [actionReason, setActionReason] = React.useState<string>('');

    React.useEffect(() => { loadPartners(); }, [page, statusFilter]);

    const loadPartners = () => {
        setLoading(true);
        ApiGatewayApis.getAllPartners(page, 20, { search, status: statusFilter })
            .then((response: any) => {
                setLoading(false);
                if (response?.data) {
                    setPartners(response.data.data || []);
                    setSummary(response.data.summary || null);
                    setMeta(response.data.meta || null);
                }
            })
            .catch((error: any) => {
                setLoading(false);
                toast.error('Failed to load partners');
            });
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        loadPartners();
    };

    const handleCreate = (e: React.FormEvent) => {
        e.preventDefault();
        setActionLoader(true);
        ApiGatewayApis.createPartner(createForm)
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('Partner created successfully');
                    setShowCreateModal(false);
                    setCreateForm({ company_name: '', contact_email: '', rate_limit_per_minute: 120 });
                    loadPartners();
                } else {
                    toast.warn(response?.data?.message || 'Failed to create partner');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error creating partner');
            });
    };

    const handleAction = (action: string, partnerId: number) => {
        setActionLoader(true);
        let promise: any;

        switch (action) {
            case 'approve':
                promise = ApiGatewayApis.approvePartner(partnerId, { generate_api_key: true });
                break;
            case 'reject':
                promise = ApiGatewayApis.rejectPartner(partnerId, { reason: actionReason });
                break;
            case 'suspend':
                promise = ApiGatewayApis.suspendPartner(partnerId, { reason: actionReason });
                break;
            case 'reactivate':
                promise = ApiGatewayApis.reactivatePartner(partnerId);
                break;
            default:
                return;
        }

        promise.then((response: any) => {
            setActionLoader(false);
            if (response?.data?.success) {
                toast.success(`Partner ${action}d successfully`);
                // If approve returned an API key, show it
                if (action === 'approve' && response.data.data?.api_key) {
                    toast.info(`API Key: ${response.data.data.api_key}`, { autoClose: false });
                }
                setShowActionModal(null);
                setActionReason('');
                loadPartners();
            } else {
                toast.warn(response?.data?.message || `Failed to ${action} partner`);
            }
        }).catch((error: any) => {
            setActionLoader(false);
            toast.error(error?.response?.data?.message || `Error ${action}ing partner`);
        });
    };

    const handleWallet = (e: React.FormEvent) => {
        e.preventDefault();
        if (!showWalletModal) return;
        setActionLoader(true);

        const data = {
            amount: parseFloat(walletForm.amount),
            description: walletForm.description,
            reference: walletForm.reference,
        };

        const promise = showWalletModal.type === 'credit'
            ? ApiGatewayApis.creditWallet(showWalletModal.partnerId, data)
            : ApiGatewayApis.debitWallet(showWalletModal.partnerId, data);

        promise.then((response: any) => {
            setActionLoader(false);
            if (response?.data?.success) {
                toast.success(`Wallet ${showWalletModal.type}ed successfully. New balance: ₦${response.data.data?.new_balance?.toLocaleString()}`);
                setShowWalletModal(null);
                setWalletForm({ amount: '', description: '', reference: '' });
                loadPartners();
            } else {
                toast.warn(response?.data?.message || 'Wallet operation failed');
            }
        }).catch((error: any) => {
            setActionLoader(false);
            toast.error(error?.response?.data?.message || 'Wallet operation error');
        });
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'suspended': return 'bg-red-100 text-red-800';
            case 'rejected': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    return (
        <>
            <AdminSidebar title="Partners" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Partner Management" />

                <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>

                    {/* Back + Header */}
                    <div className='flex items-center gap-3 mb-2'>
                        <button onClick={() => navigate('/admin/api-gateway')} className='text-gray-500 hover:text-[#E04343]'>
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className='text-[20px] font-bold text-[#263238]'>Partner Management</h1>
                    </div>

                    {/* Summary Cards */}
                    {summary && (
                        <div className='grid grid-cols-2 md:grid-cols-4 gap-3 mb-5'>
                            {[
                                { label: 'Total', value: summary.total, color: 'border-gray-400' },
                                { label: 'Active', value: summary.active, color: 'border-green-500' },
                                { label: 'Pending', value: summary.pending, color: 'border-yellow-500' },
                                { label: 'Suspended', value: summary.suspended, color: 'border-red-500' },
                            ].map((s) => (
                                <div key={s.label} className={`bg-white shadow-sm rounded-lg px-4 py-3 border-l-[3px] ${s.color}`}>
                                    <h5 className='text-[11px] text-[#808080]'>{s.label}</h5>
                                    <h3 className='text-[20px] font-bold'>{s.value || 0}</h3>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Search Bar + Filter + Create Button */}
                    <div className='bg-white shadow-sm rounded-[10px] p-4 mb-4'>
                        <div className='flex flex-col md:flex-row gap-3 justify-between'>
                            <form onSubmit={handleSearch} className='flex gap-2 flex-1'>
                                <div className='relative flex-1 max-w-[400px]'>
                                    <Search size={16} className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400' />
                                    <input
                                        type='text'
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder='Search by company name or email...'
                                        className='bg-white border border-[#DADADA] text-sm rounded-[5px] pl-9 pr-3 py-2 w-full'
                                    />
                                </div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2'
                                >
                                    <option value=''>All Statuses</option>
                                    <option value='active'>Active</option>
                                    <option value='pending'>Pending</option>
                                    <option value='suspended'>Suspended</option>
                                    <option value='rejected'>Rejected</option>
                                </select>
                                <button type='submit' className='bg-[#E04343] text-white text-sm px-4 py-2 rounded-[5px] hover:bg-[#c73838]'>
                                    Search
                                </button>
                            </form>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className='bg-[#E04343] text-white text-sm px-4 py-2 rounded-[5px] hover:bg-[#c73838] flex items-center gap-2'
                            >
                                <Plus size={16} /> New Partner
                            </button>
                        </div>
                    </div>

                    {/* Partners Table */}
                    <div className='bg-white shadow-sm rounded-[10px] overflow-hidden'>
                        {loading ? (
                            <div className='flex justify-center py-16'>
                                <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                            </div>
                        ) : partners.length === 0 ? (
                            <div className='text-center py-16'>
                                <Building2 size={40} className='mx-auto text-gray-300 mb-3' />
                                <p className='text-[14px] text-[#808080]'>No partners found</p>
                            </div>
                        ) : (
                            <div className='overflow-x-auto'>
                                <table className='w-full text-left'>
                                    <thead className='bg-gray-50 border-b'>
                                        <tr>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Company</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Contact</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Status</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Wallet (₦)</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Rate Limit</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3'>Created</th>
                                            <th className='text-[12px] text-[#808080] font-semibold px-4 py-3 text-right'>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {partners.map((partner: any) => (
                                            <tr key={partner.id} className='border-b hover:bg-gray-50 transition'>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[13px] font-medium text-[#263238]'>{partner.company_name}</span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[13px] text-[#808080]'>{partner.contact_email}</span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${getStatusBadge(partner.status)}`}>
                                                        {partner.status}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[13px] font-medium'>
                                                        {parseFloat(partner.wallet_balance || 0).toLocaleString()}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[12px] text-[#808080]'>{partner.rate_limit_per_minute}/min</span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <span className='text-[12px] text-[#808080]'>
                                                        {partner.created_at ? new Date(partner.created_at).toLocaleDateString() : '-'}
                                                    </span>
                                                </td>
                                                <td className='px-4 py-3'>
                                                    <div className='flex justify-end gap-1'>
                                                        <button
                                                            onClick={() => navigate(`/admin/api-gateway/partners/${partner.id}`)}
                                                            className='p-1.5 rounded hover:bg-gray-100' title='View'
                                                        >
                                                            <Eye size={15} className='text-gray-500' />
                                                        </button>

                                                        {partner.status === 'pending' && (
                                                            <>
                                                                <button
                                                                    onClick={() => setShowActionModal({ partnerId: partner.id, action: 'approve', partnerName: partner.company_name })}
                                                                    className='p-1.5 rounded hover:bg-green-50' title='Approve'
                                                                >
                                                                    <CheckCircle size={15} className='text-green-600' />
                                                                </button>
                                                                <button
                                                                    onClick={() => setShowActionModal({ partnerId: partner.id, action: 'reject', partnerName: partner.company_name })}
                                                                    className='p-1.5 rounded hover:bg-red-50' title='Reject'
                                                                >
                                                                    <XCircle size={15} className='text-red-500' />
                                                                </button>
                                                            </>
                                                        )}

                                                        {partner.status === 'active' && (
                                                            <>
                                                                <button
                                                                    onClick={() => setShowWalletModal({ partnerId: partner.id, type: 'credit', partnerName: partner.company_name })}
                                                                    className='p-1.5 rounded hover:bg-green-50' title='Credit Wallet'
                                                                >
                                                                    <Wallet size={15} className='text-green-600' />
                                                                </button>
                                                                <button
                                                                    onClick={() => setShowActionModal({ partnerId: partner.id, action: 'suspend', partnerName: partner.company_name })}
                                                                    className='p-1.5 rounded hover:bg-red-50' title='Suspend'
                                                                >
                                                                    <Ban size={15} className='text-red-500' />
                                                                </button>
                                                            </>
                                                        )}

                                                        {partner.status === 'suspended' && (
                                                            <button
                                                                onClick={() => setShowActionModal({ partnerId: partner.id, action: 'reactivate', partnerName: partner.company_name })}
                                                                className='p-1.5 rounded hover:bg-green-50' title='Reactivate'
                                                            >
                                                                <RotateCcw size={15} className='text-green-600' />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {meta && meta.last_page > 1 && (
                            <div className='flex justify-between items-center px-4 py-3 border-t'>
                                <span className='text-[12px] text-[#808080]'>
                                    Showing {meta.from}-{meta.to} of {meta.total}
                                </span>
                                <div className='flex gap-1'>
                                    <button
                                        disabled={page <= 1}
                                        onClick={() => setPage(page - 1)}
                                        className='p-1.5 rounded hover:bg-gray-100 disabled:opacity-30'
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <span className='px-3 py-1 text-[12px] font-medium'>{page} / {meta.last_page}</span>
                                    <button
                                        disabled={page >= meta.last_page}
                                        onClick={() => setPage(page + 1)}
                                        className='p-1.5 rounded hover:bg-gray-100 disabled:opacity-30'
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Create Partner Modal */}
            {showCreateModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[480px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold'>Create New Partner</h3>
                            <button onClick={() => setShowCreateModal(false)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleCreate} className='p-5 space-y-4'>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Company Name <span className='text-red-400'>*</span></label>
                                <input
                                    type='text' required
                                    value={createForm.company_name}
                                    onChange={(e) => setCreateForm({ ...createForm, company_name: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                    placeholder='e.g. Acme Logistics Ltd'
                                />
                            </div>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Contact Email <span className='text-red-400'>*</span></label>
                                <input
                                    type='email' required
                                    value={createForm.contact_email}
                                    onChange={(e) => setCreateForm({ ...createForm, contact_email: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                    placeholder='admin@company.com'
                                />
                            </div>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Rate Limit (per minute)</label>
                                <input
                                    type='number' min={10} max={1000}
                                    value={createForm.rate_limit_per_minute}
                                    onChange={(e) => setCreateForm({ ...createForm, rate_limit_per_minute: parseInt(e.target.value) || 120 })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                />
                            </div>
                            <button
                                type='submit' disabled={actionLoader}
                                className='bg-[#E04343] text-white py-2.5 rounded-[5px] w-full text-sm font-medium hover:bg-[#c73838] disabled:opacity-50 flex justify-center gap-2'
                            >
                                Create Partner
                                <Oval visible={actionLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Wallet Credit/Debit Modal */}
            {showWalletModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[480px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold capitalize'>{showWalletModal.type} Wallet — {showWalletModal.partnerName}</h3>
                            <button onClick={() => setShowWalletModal(null)}><X size={18} /></button>
                        </div>
                        <form onSubmit={handleWallet} className='p-5 space-y-4'>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Amount (₦) <span className='text-red-400'>*</span></label>
                                <input
                                    type='number' required min={1} step='0.01'
                                    value={walletForm.amount}
                                    onChange={(e) => setWalletForm({ ...walletForm, amount: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                    placeholder='50000'
                                />
                            </div>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Description <span className='text-red-400'>*</span></label>
                                <input
                                    type='text' required
                                    value={walletForm.description}
                                    onChange={(e) => setWalletForm({ ...walletForm, description: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                    placeholder='Bank transfer received - TRF/ACME/2025/001'
                                />
                            </div>
                            <div>
                                <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Reference</label>
                                <input
                                    type='text'
                                    value={walletForm.reference}
                                    onChange={(e) => setWalletForm({ ...walletForm, reference: e.target.value })}
                                    className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                    placeholder='TRF-001'
                                />
                            </div>
                            <button
                                type='submit' disabled={actionLoader}
                                className={`${showWalletModal.type === 'credit' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white py-2.5 rounded-[5px] w-full text-sm font-medium disabled:opacity-50 flex justify-center gap-2 capitalize`}
                            >
                                {showWalletModal.type} Wallet
                                <Oval visible={actionLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Action Confirm Modal (Approve/Reject/Suspend/Reactivate) */}
            {showActionModal && (
                <div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[420px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b'>
                            <h3 className='text-[16px] font-semibold capitalize'>{showActionModal.action} Partner</h3>
                            <button onClick={() => { setShowActionModal(null); setActionReason(''); }}><X size={18} /></button>
                        </div>
                        <div className='p-5'>
                            <p className='text-[13px] text-[#808080] mb-4'>
                                Are you sure you want to <strong className='text-[#263238]'>{showActionModal.action}</strong>{' '}
                                <strong className='text-[#263238]'>{showActionModal.partnerName}</strong>?
                                {showActionModal.action === 'approve' && ' An API key will be auto-generated.'}
                                {showActionModal.action === 'suspend' && ' All their API keys will be deactivated.'}
                            </p>

                            {['reject', 'suspend'].includes(showActionModal.action) && (
                                <div className='mb-4'>
                                    <label className='block mb-1 text-sm font-semibold text-[#19213D]'>Reason <span className='text-red-400'>*</span></label>
                                    <textarea
                                        value={actionReason}
                                        onChange={(e) => setActionReason(e.target.value)}
                                        rows={2}
                                        required
                                        className='bg-white border border-[#DADADA] text-sm rounded-[5px] w-full p-2.5'
                                        placeholder='Provide a reason...'
                                    />
                                </div>
                            )}

                            <div className='flex gap-3'>
                                <button
                                    onClick={() => { setShowActionModal(null); setActionReason(''); }}
                                    className='flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-[5px] text-sm font-medium hover:bg-gray-200'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleAction(showActionModal.action, showActionModal.partnerId)}
                                    disabled={actionLoader || (['reject', 'suspend'].includes(showActionModal.action) && !actionReason)}
                                    className={`flex-1 text-white py-2.5 rounded-[5px] text-sm font-medium disabled:opacity-50 flex justify-center gap-2 ${['approve', 'reactivate'].includes(showActionModal.action) ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                                        }`}
                                >
                                    <span className='capitalize'>{showActionModal.action}</span>
                                    <Oval visible={actionLoader} height="18" width="18" color="#fff" secondaryColor="#FCBF94" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
}

export default PartnerManagement;